// Cost Catalog admin — surfaces every key in cost_catalog.labor +
// cost_catalog.materials so the user can see and edit the rates that
// drive soil-aware labor, the pricing model, and BOM costs.
//
// Endpoints (already exist in plant-settings.ts):
//   GET  /api/settings/cost-catalogs      → array of catalog rows
//   PUT  /api/settings/cost-catalogs/:id  → patch labor/materials/region_mult/notes/name
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import ImportModal from '../components/ImportModal';

type Catalog = {
  id: string;
  name: string;
  is_default: boolean;
  region_mult: number | string;
  labor: any;
  materials: any;
  notes: string | null;
  updated_at: string;
};

const fmtMoney = (v: any) => {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 10)   return `$${n.toFixed(2)}`;
  return `$${n.toFixed(2)}`;
};

// ---------- canonical key lists with display labels + units ----------
const LABOR_PER_FT: Array<[string, string, string]> = [
  ['plow',                'Plow / vibratory plow',           'Cheapest buried method when soils + slope allow'],
  ['trench',              'Trench',                          'Generic trench (used as fallback for open_trench)'],
  ['open_trench',         'Open trench',                     'Standard cut & restore'],
  ['micro_trench',        'Micro-trench',                    'Pavement-cut, narrow slot'],
  ['rock_saw',            'Rock saw / rock trencher',        'Shallow bedrock zones'],
  ['directional_bore',    'Directional bore (HDD)',          'Wetlands, road crossings, sensitive areas'],
  ['hdd_bore',            'HDD bore (legacy alias)',         'Falls back to directional_bore'],
  ['direct_bury',         'Direct bury (cable in trench)',   'Without conduit'],
  ['buried',              'Buried (generic)',                'Catch-all'],
  ['aerial_install',      'Aerial install (lash/messenger)', 'Stringing labor on existing pole strand'],
  ['conduit_install',     'Conduit install',                 'HDPE/innerduct placement'],
  ['sod_restoration',     'Sod restoration',                 'Yard/grass repair after trench'],
  ['asphalt_restoration', 'Asphalt restoration',             'Asphalt patch after trench'],
  ['concrete_restoration','Concrete restoration',            'Concrete patch'],
  ['open_cut_asphalt',    'Open-cut asphalt',                'Total open-cut + restoration in asphalt'],
  ['open_cut_concrete',   'Open-cut concrete',               'Total open-cut + restoration in concrete'],
];

const LABOR_PER_EACH: Array<[string, string, string]> = [
  ['pole_set',             'Pole set',                       'New pole installed (incl. dig, anchor)'],
  ['pole_make_ready',      'Pole make-ready',                'Existing pole prep for our attachment'],
  ['pedestal_install',     'Pedestal install',               'Above-ground termination cabinet'],
  ['vault_install_small',  'Vault install (small)',          'Hand-hole / small flush vault'],
  ['vault_install_large',  'Vault install (large)',          'Splice/feeder vault'],
  ['splice_closure_setup', 'Splice closure setup',           'Per closure dome at splice point'],
  ['splice_per_fiber',     'Splice per fiber',               'Fusion splice labor per strand'],
  ['ont_install',          'ONT install',                    'Customer ONT mount + activation'],
  ['drop_install_aerial',  'Drop install (aerial)',          'Per service drop — pole to home'],
  ['drop_install_buried',  'Drop install (buried)',          'Per service drop — pedestal to home'],
];

const LABOR_PER_MILE: Array<[string, string, string]> = [
  ['construction_underground', 'Construction labor (underground)', 'Total per-mile UG construction labor (BEAD-grade)'],
  ['labor_travel',             'Construction labor travel',         'Per-mile travel expense for crews'],
  ['design_engineering',       'Design engineering (contractor)',   'Per-mile engineering / OSP design service'],
];

const PERMITS_PER_MILE: Array<[string, string, string]> = [
  ['state_dot',  'State DOT ROW permits',  'e.g. WYDOT, ARDOT - per mile of build inside state ROW'],
  ['county',     'County ROW permits',     'Per mile of build in county ROW'],
  ['municipal',  'Municipal ROW permits',  'Per mile of build in municipal/city ROW'],
];

const PERMITS_PER_EACH: Array<[string, string, string]> = [
  ['railroad_bnsf', 'Railroad permit - BNSF', 'Per crossing'],
  ['railroad_up',   'Railroad permit - UP',   'Per crossing'],
];


const PERCENT_ADDERS: Array<[string, string]> = [
  ['permitting',   'Permitting'],
  ['contingency',  'Contingency'],
  ['engineering',  'Engineering'],
  ['project_mgmt', 'Project management'],
];

// ---------- materials field tree ----------
type MatField = { key: string; label: string; unit: string; sub?: Array<MatField> };

const MATERIALS_FIELDS: MatField[] = [
  { key: 'cable_per_ft', label: 'Cable $/ft by strand count', unit: '$/ft', sub: [
    { key: '12',  label: '12-strand',  unit: '$/ft' },
    { key: '24',  label: '24-strand',  unit: '$/ft' },
    { key: '48',  label: '48-strand',  unit: '$/ft' },
    { key: '72',  label: '72-strand',  unit: '$/ft' },
    { key: '96',  label: '96-strand',  unit: '$/ft' },
    { key: '144', label: '144-strand', unit: '$/ft' },
    { key: '216', label: '216-strand', unit: '$/ft' },
    { key: '288', label: '288-strand', unit: '$/ft' },
    { key: '432', label: '432-strand', unit: '$/ft' },
    { key: '576', label: '576-strand', unit: '$/ft' },
    { key: '864', label: '864-strand', unit: '$/ft' },
  ]},
  { key: 'drop_cable_per_ft', label: 'Drop cable $/ft by strand count', unit: '$/ft', sub: [
    { key: '1',  label: '1-strand drop',  unit: '$/ft' },
    { key: '2',  label: '2-strand drop',  unit: '$/ft' },
    { key: '6',  label: '6-strand drop',  unit: '$/ft' },
    { key: '12', label: '12-strand drop', unit: '$/ft' },
  ]},
  { key: 'conduit_per_ft', label: 'Conduit $/ft', unit: '$/ft', sub: [
    { key: '1.25in_hdpe',    label: '1.25" HDPE',          unit: '$/ft' },
    { key: '2in_hdpe',       label: '2" HDPE',             unit: '$/ft' },
    { key: '4in_hdpe',       label: '4" HDPE',             unit: '$/ft' },
    { key: 'innerduct_3cell',label: '3-cell innerduct',    unit: '$/ft' },
  ]},
  { key: 'vault', label: 'Vaults (each)', unit: '$', sub: [
    { key: '30x48', label: '30 × 48',       unit: '$' },
    { key: '36x60', label: '36 × 60',       unit: '$' },
    { key: '48x60', label: '48 × 60',       unit: '$' },
    { key: '60x60', label: '60 × 60',       unit: '$' },
  ]},
  { key: 'splice_closure', label: 'Splice closures (each, by capacity)', unit: '$', sub: [
    { key: '48',  label: '48-strand',  unit: '$' },
    { key: '96',  label: '96-strand',  unit: '$' },
    { key: '144', label: '144-strand', unit: '$' },
    { key: '216', label: '216-strand', unit: '$' },
    { key: '288', label: '288-strand', unit: '$' },
  ]},
  { key: 'splitter', label: 'Splitters (each)', unit: '$', sub: [
    { key: '1x4',  label: '1×4',  unit: '$' },
    { key: '1x8',  label: '1×8',  unit: '$' },
    { key: '1x16', label: '1×16', unit: '$' },
    { key: '1x32', label: '1×32', unit: '$' },
    { key: '1x64', label: '1×64', unit: '$' },
  ]},
  { key: 'splice_tray_24',  label: 'Splice tray (24-fiber)', unit: '$' },
  { key: 'pedestal',        label: 'Pedestal',               unit: '$' },
  { key: 'drop_terminal',   label: 'Drop terminal',          unit: '$' },
  { key: 'ont',             label: 'ONT',                    unit: '$' },
  { key: 'olt_port',        label: 'OLT port',               unit: '$' },
  { key: 'electronics', label: 'Electronics (OLT bundle, SFPs, patch panels)', unit: '$/each', sub: [
    { key: 'olt_combo_16_port',            label: 'OLT Combo PON 16-port (chassis)', unit: '$/each' },
    { key: 'olt_shelf_2u',                 label: 'OLT 2U power shelf',              unit: '$/each' },
    { key: 'olt_power_module_48vdc_700w',  label: 'OLT 48VDC 700W power module',     unit: '$/each' },
    { key: 'olt_power_supply_48vdc_250w',  label: 'OLT 48VDC 250W power supply',     unit: '$/each' },
    { key: 'olt_battery_100ah_48v',        label: 'OLT 100Ah 48V Li-ion battery',    unit: '$/each' },
    { key: 'patch_panel_144_sc_apc',       label: '144-port SC/APC patch panel',     unit: '$/each' },
    { key: 'olt_uplink_sfp_10g_lr',        label: '10G SFP+ SMF LR transceiver',     unit: '$/each' },
    { key: 'olt_pon_sfp_xgspon',           label: 'XGSPON OLT SFP, C+',              unit: '$/each' },
    { key: 'patch_cord_sc_apc',            label: 'SC/APC patch cord (1m)',          unit: '$/each' },
  ] },
  { key: 'mst_terminal_per_ft', label: 'MST drop terminals (per ft of stub)', unit: '$/ft', sub: [
    { key: '4_port',  label: '4-port MST',  unit: '$/ft' },
    { key: '6_port',  label: '6-port MST',  unit: '$/ft' },
    { key: '8_port',  label: '8-port MST',  unit: '$/ft' },
    { key: '12_port', label: '12-port MST', unit: '$/ft' },
  ] },
  { key: 'splice_closure_per_ft', label: 'Splice closure (Apex, per ft of run)', unit: '$/ft', sub: [
    { key: '288_apex_x2s', label: 'Apex X-2S 288 splice', unit: '$/ft' },
  ] },
  { key: 'splice_tray_per_ft', label: 'Splice tray (Apex, per ft of run)', unit: '$/ft', sub: [
    { key: '48_apex_x2s', label: 'Apex X2-s 48-splice tray', unit: '$/ft' },
  ] },
  { key: 'vault_round', label: 'Round vault (small)', unit: '$/each', sub: [
    { key: '10in_light_duty', label: '10-inch round, light duty', unit: '$/each' },
  ] },
  { key: 'locate_puck',        label: 'Locate puck (Mamba 72-inch)',        unit: '$/each' },
  { key: 'grounding_rod_10pk', label: 'Grounding rod 3/8 x 4ft (10-pack)',  unit: '$/pack' },
];

// ---------- styles ----------
const SECTION: React.CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '12px 16px', marginBottom: 16,
};
const SECTION_H: React.CSSProperties = {
  margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 700,
  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
};
const TABLE: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 12,
};
const TH: React.CSSProperties = {
  textAlign: 'left', padding: '4px 6px', color: '#475569', fontWeight: 600,
  borderBottom: '1px solid #e2e8f0',
};
const TD: React.CSSProperties = {
  padding: '4px 6px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top',
};
const NUM_INPUT: React.CSSProperties = {
  width: 90, padding: '3px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums',
  border: '1px solid #cbd5e1', borderRadius: 3, textAlign: 'right',
};

// ============================================================
export default function CostCatalogPage() {
  const qc = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const { data: catalogs, isLoading } = useQuery<Catalog[]>({
    queryKey: ['cost-catalogs'],
    queryFn: () => api.get('/settings/cost-catalogs'),
  });

  // Local edit state — copies of the JSONBs so user can edit before save
  const [labor, setLabor] = useState<any>({});
  const [materials, setMaterials] = useState<any>({});
  const [name, setName] = useState('');
  const [regionMult, setRegionMult] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const initialized = useRef(false);

  const cat = catalogs && catalogs[0]; // single-tenant: only one catalog row

  useEffect(() => {
    if (!cat || initialized.current) return;
    setLabor(cat.labor ?? {});
    setMaterials(cat.materials ?? {});
    setName(cat.name);
    setRegionMult(Number(cat.region_mult ?? 1));
    setNotes(cat.notes ?? '');
    initialized.current = true;
  }, [cat]);

  const saveMut = useMutation({
    mutationFn: () => api.put(`/settings/cost-catalogs/${cat!.id}`, {
      name, region_mult: regionMult, labor, materials, notes,
    }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['cost-catalogs'] });
    },
  });

  if (isLoading) return <div className="card"><div className="card__body">Loading catalog…</div></div>;
  if (!cat) return <div className="card"><div className="card__body">No cost catalog row found.</div></div>;

  // Per-ft helpers
  const setPerFt = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      per_ft: { ...(cur.per_ft ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setPerEach = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      per_each: { ...(cur.per_each ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setPctAdder = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      percent_adders: { ...(cur.percent_adders ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setPerMile = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      per_mile: { ...(cur.per_mile ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setPermitMile = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      permits_per_mile: { ...(cur.permits_per_mile ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setPermitEach = (k: string, v: number | undefined) => {
    setDirty(true);
    setLabor((cur: any) => ({
      ...cur,
      permits_per_each: { ...(cur.permits_per_each ?? {}), [k]: v === undefined ? undefined : Number(v) },
    }));
  };
  const setMatScalar = (k: string, v: number | undefined) => {
    setDirty(true);
    setMaterials((cur: any) => ({ ...cur, [k]: v === undefined ? undefined : Number(v) }));
  };
  const setMatNested = (k: string, sub: string, v: number | undefined) => {
    setDirty(true);
    setMaterials((cur: any) => ({
      ...cur,
      [k]: { ...(cur[k] ?? {}), [sub]: v === undefined ? undefined : Number(v) },
    }));
  };

  // ---------- discover EXTRA keys in DB not in our canonical lists ----------
  // (so user sees everything in the JSONB, not just what we know about)
  const knownPerFt = new Set(LABOR_PER_FT.map(([k]) => k));
  const extraPerFt = Object.keys(labor.per_ft ?? {}).filter((k) => !knownPerFt.has(k));
  const knownPerEach = new Set(LABOR_PER_EACH.map(([k]) => k));
  const extraPerEach = Object.keys(labor.per_each ?? {}).filter((k) => !knownPerEach.has(k));
  const knownAdders = new Set(PERCENT_ADDERS.map(([k]) => k));
  const extraAdders = Object.keys(labor.percent_adders ?? {}).filter((k) => !knownAdders.has(k));
  const knownMat = new Set(MATERIALS_FIELDS.map((f) => f.key));
  const extraMat = Object.keys(materials ?? {}).filter((k) => !knownMat.has(k));

  return (
    <div style={{ maxWidth: 1100 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Rate Card</h1>
          <div className="page__subtitle">
            Labor + materials unit rates that drive soil-aware labor, pricing model, and project BOM.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: '#b45309' }}>● unsaved</span>}
          <button className="btn" onClick={() => setShowImport(true)} style={{ padding: '6px 16px' }}>⭱ Import</button>
          <button
            className="btn btn--primary"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            style={{ padding: '6px 16px' }}
          >
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {showImport && <ImportModal title="Import rate card" endpoint="/finance/catalog/import"
        templateHeaders={['item_key','line_item','category','unit','unit_cost']}
        sampleRow={{item_key:'labor_per_ft_aerial_install',line_item:'Aerial install (lash/messenger)',category:'labor',unit:'ft',unit_cost:'1.50'}}
        help="Updates unit costs by item_key; unknown keys are added. Download the template to get your current keys."
        onClose={() => setShowImport(false)} onDone={() => qc.invalidateQueries({ queryKey: ['cost-catalogs'] })} />}

      {saveMut.isError && (
        <div style={{ ...SECTION, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
          Save failed: {(saveMut.error as any)?.message ?? 'unknown'}
        </div>
      )}

      {/* Header / metadata */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Catalog metadata</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>
            Last saved: {new Date(cat.updated_at).toLocaleString()}
          </span>
        </h2>
        <table style={TABLE}>
          <tbody>
            <tr>
              <td style={{ ...TD, color: '#64748b', width: 180 }}>Name</td>
              <td style={TD}>
                <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }}
                  style={{ width: 360, padding: '3px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3 }} />
              </td>
            </tr>
            <tr>
              <td style={{ ...TD, color: '#64748b' }}>Region multiplier</td>
              <td style={TD}>
                <input type="number" step={0.01} value={regionMult}
                  onChange={(e) => { setRegionMult(Number(e.target.value)); setDirty(true); }}
                  style={NUM_INPUT} />
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8 }}>
                  Multiplies every per_ft labor rate. 1.00 = no adjustment.
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ ...TD, color: '#64748b', verticalAlign: 'top' }}>Notes</td>
              <td style={TD}>
                <textarea value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
                  rows={2}
                  style={{ width: '90%', padding: '4px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, fontFamily: 'inherit' }} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Labor — Per ft */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Labor — Per foot</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>
            Soil-aware blend pulls from these (catalog fallback when Settings → Placement is blank)
          </span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Method</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, textAlign: 'right' }}>$/ft</th>
          </tr></thead>
          <tbody>
            {LABOR_PER_FT.map(([k, label, hint]) => (
              <tr key={k}>
                <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                <td style={{ ...TD, color: '#64748b' }}>{hint}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={500} step={0.10}
                    value={labor.per_ft?.[k] ?? ''}
                    placeholder="—"
                    onChange={(e) => setPerFt(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
            {extraPerFt.length > 0 && (
              <>
                <tr><td colSpan={3} style={{ ...TD, color: '#94a3b8', fontStyle: 'italic', paddingTop: 12 }}>
                  Other keys present in DB:
                </td></tr>
                {extraPerFt.map((k) => (
                  <tr key={k}>
                    <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{k}</td>
                    <td style={{ ...TD, color: '#94a3b8', fontSize: 11 }}>(custom key)</td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <input type="number" min={0} max={500} step={0.10}
                        value={labor.per_ft?.[k] ?? ''}
                        onChange={(e) => setPerFt(k, e.target.value === '' ? undefined : Number(e.target.value))}
                        style={NUM_INPUT} />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Labor — Per each */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Labor — Per each (unit cost)</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>One-time labor per discrete install</span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Item</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, textAlign: 'right' }}>$ each</th>
          </tr></thead>
          <tbody>
            {LABOR_PER_EACH.map(([k, label, hint]) => (
              <tr key={k}>
                <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                <td style={{ ...TD, color: '#64748b' }}>{hint}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={50000} step={1}
                    value={labor.per_each?.[k] ?? ''}
                    placeholder="—"
                    onChange={(e) => setPerEach(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
            {extraPerEach.map((k) => (
              <tr key={k}>
                <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{k}</td>
                <td style={{ ...TD, color: '#94a3b8', fontSize: 11 }}>(custom key)</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={50000} step={1}
                    value={labor.per_each?.[k] ?? ''}
                    onChange={(e) => setPerEach(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Labor — Per mile (UG construction, travel, design) */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Labor — Per mile</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>BEAD-grade construction labor + travel + design, billed per mile</span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Item</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, textAlign: 'right' }}>$ per mile</th>
          </tr></thead>
          <tbody>
            {LABOR_PER_MILE.map(([k, label, hint]) => (
              <tr key={k}>
                <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                <td style={{ ...TD, color: '#64748b' }}>{hint}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={500000} step={50}
                    value={labor.per_mile?.[k] ?? ''}
                    placeholder="-"
                    onChange={(e) => setPerMile(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ROW Permits — Per mile */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>ROW Permits — Per mile</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>Right-of-way permit fees by authority</span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Authority</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, textAlign: 'right' }}>$ per mile</th>
          </tr></thead>
          <tbody>
            {PERMITS_PER_MILE.map(([k, label, hint]) => (
              <tr key={k}>
                <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                <td style={{ ...TD, color: '#64748b' }}>{hint}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={10000} step={1}
                    value={labor.permits_per_mile?.[k] ?? ''}
                    placeholder="-"
                    onChange={(e) => setPermitMile(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permits — Per each crossing */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Railroad / per-crossing permits</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>One-time fees per railroad crossing</span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Authority</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, textAlign: 'right' }}>$ per crossing</th>
          </tr></thead>
          <tbody>
            {PERMITS_PER_EACH.map(([k, label, hint]) => (
              <tr key={k}>
                <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                <td style={{ ...TD, color: '#64748b' }}>{hint}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={50000} step={1}
                    value={labor.permits_per_each?.[k] ?? ''}
                    placeholder="-"
                    onChange={(e) => setPermitEach(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Labor — Percent adders */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Percent adders (decimal multipliers)</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>
            Applied on top of direct labor. e.g. 0.10 = 10%
          </span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Adder</th>
            <th style={{ ...TH, textAlign: 'right' }}>multiplier</th>
            <th style={{ ...TH, textAlign: 'right' }}>= %</th>
          </tr></thead>
          <tbody>
            {PERCENT_ADDERS.map(([k, label]) => {
              const v = labor.percent_adders?.[k];
              return (
                <tr key={k}>
                  <td style={{ ...TD, fontWeight: 500 }}>{label}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    <input type="number" min={0} max={1} step={0.01}
                      value={v ?? ''}
                      placeholder="—"
                      onChange={(e) => setPctAdder(k, e.target.value === '' ? undefined : Number(e.target.value))}
                      style={NUM_INPUT} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                    {v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              );
            })}
            {extraAdders.map((k) => (
              <tr key={k}>
                <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{k}</td>
                <td style={{ ...TD, textAlign: 'right' }}>
                  <input type="number" min={0} max={1} step={0.01}
                    value={labor.percent_adders?.[k] ?? ''}
                    onChange={(e) => setPctAdder(k, e.target.value === '' ? undefined : Number(e.target.value))}
                    style={NUM_INPUT} />
                </td>
                <td style={TD}></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Materials */}
      <div style={SECTION}>
        <h2 style={SECTION_H}>
          <span>Materials</span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>
            Unit prices for BOM. Used by pricing model + project budgets.
          </span>
        </h2>
        <table style={TABLE}>
          <thead><tr>
            <th style={TH}>Item</th>
            <th style={{ ...TH, textAlign: 'right' }}>price</th>
            <th style={{ ...TH, textAlign: 'left', width: 60 }}>unit</th>
          </tr></thead>
          <tbody>
            {MATERIALS_FIELDS.map((f) => {
              if (f.sub) {
                return (
                  <>
                    <tr key={f.key}>
                      <td colSpan={3} style={{ ...TD, fontWeight: 700, paddingTop: 10, color: '#0f172a', borderBottom: '1px solid #cbd5e1' }}>
                        {f.label}
                      </td>
                    </tr>
                    {f.sub.map((s) => (
                      <tr key={`${f.key}.${s.key}`}>
                        <td style={{ ...TD, paddingLeft: 24 }}>{s.label}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>
                          <input type="number" min={0} step={0.01}
                            value={(materials[f.key] ?? {})[s.key] ?? ''}
                            placeholder="—"
                            onChange={(e) => setMatNested(f.key, s.key, e.target.value === '' ? undefined : Number(e.target.value))}
                            style={NUM_INPUT} />
                        </td>
                        <td style={{ ...TD, color: '#94a3b8' }}>{s.unit}</td>
                      </tr>
                    ))}
                  </>
                );
              }
              return (
                <tr key={f.key}>
                  <td style={{ ...TD, fontWeight: 500 }}>{f.label}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    <input type="number" min={0} step={0.01}
                      value={materials[f.key] ?? ''}
                      placeholder="—"
                      onChange={(e) => setMatScalar(f.key, e.target.value === '' ? undefined : Number(e.target.value))}
                      style={NUM_INPUT} />
                  </td>
                  <td style={{ ...TD, color: '#94a3b8' }}>{f.unit}</td>
                </tr>
              );
            })}
            {extraMat.length > 0 && (
              <>
                <tr><td colSpan={3} style={{ ...TD, color: '#94a3b8', fontStyle: 'italic', paddingTop: 12 }}>
                  Other top-level material keys in DB (not editable here — use SQL):
                </td></tr>
                {extraMat.map((k) => (
                  <tr key={k}>
                    <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{k}</td>
                    <td style={{ ...TD, color: '#94a3b8', fontSize: 11 }} colSpan={2}>
                      {typeof materials[k] === 'object' ? `(nested object, ${Object.keys(materials[k] ?? {}).length} keys)` : String(materials[k])}
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
